import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { addDays } from "date-fns";
import AppDialog from "../../shared/components/AppDialog";
import { Z } from "../../../theme/zIndex";
import { formatDateKey, parseDateOnly } from "../../shared/utils/dateUtils";
import { getApiErrorMessage } from "../../shared/utils/apiError";
import { isInternalDepartment } from "../utils/internalDepartments";
import type { Sector } from "../types/Sector";
import {
  DepartmentUnavailability,
  DepartmentUnavailabilityInput,
  useSaveDepartmentUnavailability,
} from "../departmentAvailability";

interface Props {
  open: boolean;
  zonas: Sector[];
  initial: DepartmentUnavailabilityInput | DepartmentUnavailability;
  onClose: () => void;
  onSaved: () => void;
}

const TITLE_ID = "fuera-de-servicio-dialog-title";
const MOTIVO_MAX = 500;

/**
 * Alta y edición de un fuera de servicio. Las fechas se manejan como claves
 * YYYY-MM-DD (nunca Date en UTC): las mismas reglas que el resto del gestor.
 */
const DepartmentAvailabilityDialog: React.FC<Props> = ({ open, zonas, initial, onClose, onSaved }) => {
  const theme = useTheme();
  const [form, setForm] = useState<DepartmentUnavailabilityInput>(initial);
  const [error, setError] = useState("");
  const save = useSaveDepartmentUnavailability();
  const id = "id" in initial ? initial.id : undefined;

  useEffect(() => {
    if (open) {
      setForm(initial);
      setError("");
    }
  }, [initial, open]);

  const departamentoDisponible = useMemo(
    () => zonas.some((sector) => sector.departamentos.includes(form.departamento)),
    [zonas, form.departamento]
  );

  const invalid =
    !form.departamento ||
    !form.desde ||
    !form.disponible_nuevamente ||
    form.disponible_nuevamente <= form.desde ||
    !form.motivo.trim();

  const setDesde = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) return;
    const desde = formatDateKey(date);
    setForm((prev) => ({
      ...prev,
      desde,
      // "Disponible nuevamente" nunca puede quedar antes o igual que "Desde".
      disponible_nuevamente:
        prev.disponible_nuevamente > desde ? prev.disponible_nuevamente : formatDateKey(addDays(date, 1)),
    }));
  };

  const setDisponible = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) return;
    setForm((prev) => ({ ...prev, disponible_nuevamente: formatDateKey(date) }));
  };

  const submit = async () => {
    setError("");
    try {
      await save.mutateAsync({ input: form, id });
      onSaved();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "No se pudo guardar el fuera de servicio"));
    }
  };

  return (
    <AppDialog open={open} onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby={TITLE_ID}>
      <DialogTitle
        id={TITLE_ID}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <EventBusyIcon sx={{ color: theme.palette.app.availability.outOfService }} />
          <span>{id ? "Editar fuera de servicio" : "Marcar fuera de servicio"}</span>
        </Stack>
        <IconButton onClick={onClose} disabled={save.isPending} aria-label="Cerrar" sx={{ color: "grey.500" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth size="small">
              <InputLabel id="fuera-de-servicio-depto-label">Departamento</InputLabel>
              <Select
                labelId="fuera-de-servicio-depto-label"
                label="Departamento"
                value={departamentoDisponible ? form.departamento : ""}
                onChange={(event) => setForm({ ...form, departamento: String(event.target.value) })}
                MenuProps={{ sx: { zIndex: Z.datepicker } }}
              >
                {zonas.map((sector) => {
                  const departamentos = sector.departamentos.filter(
                    (depto) => !isInternalDepartment(depto) || depto === form.departamento
                  );
                  if (departamentos.length === 0) return [];
                  return [
                    <MenuItem
                      key={sector.id}
                      disabled
                      divider
                      sx={{ bgcolor: theme.palette.background.default, fontWeight: "bold", color: "primary.main" }}
                    >
                      Sector {sector.nombre}
                    </MenuItem>,
                    ...departamentos.map((depto) => (
                      <MenuItem key={depto} value={depto} sx={{ pl: 4 }}>
                        {depto}
                      </MenuItem>
                    )),
                  ];
                })}
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DatePicker
                label="Desde"
                value={form.desde ? parseDateOnly(form.desde) : null}
                onChange={setDesde}
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                  popper: { sx: { zIndex: Z.datepicker } },
                }}
              />
              <DatePicker
                label="Disponible nuevamente"
                value={form.disponible_nuevamente ? parseDateOnly(form.disponible_nuevamente) : null}
                onChange={setDisponible}
                minDate={form.desde ? addDays(parseDateOnly(form.desde), 1) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    helperText: "Ese día ya se puede reservar",
                  },
                  popper: { sx: { zIndex: Z.datepicker } },
                }}
              />
            </Stack>

            <TextField
              fullWidth
              required
              size="small"
              label="Motivo"
              value={form.motivo}
              onChange={(event) => setForm({ ...form, motivo: event.target.value })}
              placeholder="Ej. Revisión de gas"
              multiline
              minRows={2}
              inputProps={{ maxLength: MOTIVO_MAX }}
              helperText={`${form.motivo.length}/${MOTIVO_MAX}`}
            />

            <Typography variant="caption" color="text.secondary">
              Mientras esté fuera de servicio no se puede reservar y no cuenta como disponible en Estadísticas.
            </Typography>
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" disabled={save.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending || invalid}>
          {save.isPending ? "Guardando..." : id ? "Guardar cambios" : "Marcar fuera de servicio"}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default DepartmentAvailabilityDialog;
